import type { NoteConfig, Layer } from "@/types/legend.types";

/**
 * Résolution de la couleur d'affichage d'une note de légende — extrait de
 * `NoteConfigButton.vue` pour être partagé avec les pastilles de l'analyse
 * du plan. La couleur CAD d'une note est soit directe (« R,G,B » ou tableau),
 * soit héritée de son calque (« kByLayer »).
 */

/** Gris neutre quand la couleur est absente ou illisible. */
export const FALLBACK_NOTE_COLOR = "#888888";

/** Parse une couleur chaîne « R,G,B » vers un `rgb()` CSS, null si invalide. */
export function parseColorString(color: string): string | null {
  const parts = color.split(",").map((p) => parseInt(p.trim(), 10));
  if (parts.length >= 3 && parts.every((p) => !isNaN(p))) {
    return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  }
  return null;
}

/**
 * Couleur CSS d'une note, calque résolu si « kByLayer ».
 * @param layers `layer_configs` de la légende active.
 */
export function resolveNoteColor(
  note: NoteConfig | undefined,
  layers: Layer[],
): string {
  const color = note?.props?.color;
  const layerId = note?.props?.layerId;

  // Couleur héritée du calque.
  if (
    typeof color === "string" &&
    color.toLowerCase() === "kbylayer" &&
    layerId
  ) {
    const layer = layers.find((l) => l.id === layerId);
    if (layer?.color) {
      if (typeof layer.color === "string") {
        const parsed = parseColorString(layer.color);
        if (parsed) return parsed;
      } else if (Array.isArray(layer.color) && layer.color.length >= 3) {
        return `rgb(${layer.color[0]}, ${layer.color[1]}, ${layer.color[2]})`;
      }
    }
  }

  // Couleur directe.
  if (!color) return FALLBACK_NOTE_COLOR;
  if (typeof color === "string") {
    return parseColorString(color) ?? FALLBACK_NOTE_COLOR;
  }
  if (Array.isArray(color) && color.length >= 3) {
    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  }
  return FALLBACK_NOTE_COLOR;
}
